import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/** Tags removed entirely (including subtree). */
const REMOVED_TAGS = new Set([
  'SCRIPT',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'LINK',
  'META',
  'BASE',
]);

const URL_ATTR_NAMES = new Set([
  'href',
  'src',
  'xlink:href',
  'formaction',
  'action',
  'poster',
  'cite',
]);

function isDangerousUrl(value: string): boolean {
  const normalized = value.trim().split(/\s+/).join('').toLowerCase();
  return (
    normalized.startsWith('javascript:') ||
    normalized.startsWith('vbscript:') ||
    normalized.startsWith('data:text/html')
  );
}

function urlAttributeIsDangerous(name: string, value: string): boolean {
  const lower = name.toLowerCase();
  if (lower === 'srcset') {
    return value.split(',').some((part) => {
      const url = part.trim().split(/\s+/)[0] ?? '';
      return isDangerousUrl(url);
    });
  }
  return URL_ATTR_NAMES.has(lower) && isDangerousUrl(value);
}

function styleAttributeIsDangerous(value: string): boolean {
  return /javascript:/i.test(value) || /expression\s*\(/i.test(value);
}

function sanitizeElementAttributes(el: Element): void {
  for (let i = el.attributes.length - 1; i >= 0; i--) {
    const attr = el.attributes.item(i);
    if (!attr) {
      continue;
    }
    const name = attr.name;
    const lower = name.toLowerCase();
    if (lower.startsWith('on')) {
      el.removeAttribute(name);
      continue;
    }
    if (urlAttributeIsDangerous(name, attr.value)) {
      el.removeAttribute(name);
      continue;
    }
    if (lower === 'style' && styleAttributeIsDangerous(attr.value)) {
      el.removeAttribute(name);
    }
  }
}

function stripUnsafeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node: Node): void => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }
      const el = child as Element;
      const tag = el.tagName.toUpperCase();
      if (REMOVED_TAGS.has(tag)) {
        el.remove();
        continue;
      }
      sanitizeElementAttributes(el);
      walk(el);
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

@Pipe({ name: 'safeHtml' })
export class SafeHtmlPipe implements PipeTransform {
  constructor(private readonly sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml | string {
    if (value == null || value === '') {
      return '';
    }
    const cleaned = stripUnsafeHtml(value);
    return this.sanitizer.bypassSecurityTrustHtml(cleaned);
  }
}
